// Phase 23A — Field user PJP calendar + change-request submit
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, ChevronLeft, ChevronRight, CalendarDays, X, Send, List, Grid3x3, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";

const SRC_COLOR = { admin:"#6D2C91", cycle:"#0ea5e9", approved_request:"#059669",
                     bulk_import:"#f59e0b", weekday_fallback:"#a3a3a3" };
const SRC_LETTER = { admin:"M", cycle:"C", approved_request:"R",
                       bulk_import:"B", weekday_fallback:"W" };

const BADGE = {
  submitted:"bg-amber-100 text-amber-800",
  manager_approved:"bg-sky-100 text-sky-800",
  admin_approved:"bg-emerald-100 text-emerald-800",
  rejected:"bg-rose-100 text-rose-800",
};

function daysGrid(month) {
  const [y, m] = month.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const cells = [];
  for (let i = 0; i < first.getDay(); i++) cells.push(null);
  const n = new Date(y, m, 0).getDate();
  for (let d = 1; d <= n; d++)
    cells.push(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
  return cells;
}

export default function PJPMobile() {
  const { user } = useAuth();
  const [view, setView] = useState("list"); // "list" | "calendar" — list shows full beat names
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [beats, setBeats] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [requests, setRequests] = useState([]);
  const [proposeOpen, setProposeOpen] = useState(false);
  const [proposedBeat, setProposedBeat] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.get("/pjp/calendar", { params: { user_id: user.id, month } }),
        api.get("/pjp/requests/mine", { params: { page_size: 50 } }),
      ]);
      setData(c.data);
      setRequests(r.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [user?.id, month]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!user?.id) return;
    // for_user_id — beats assigned directly to this user OR carried by any
    // distributor mapped to them (routes_masters_read.py's list_beats was
    // literally built for this exact "PJP beat picker" case). Without it,
    // the endpoint fell back to every beat in the caller's whole zone, so
    // beats from distributors that aren't even mapped to this user showed
    // up in the "propose change" picker and the calendar's beat lookup.
    api.get("/beats", { params: { page_size: 200, for_user_id: user.id } })
      .then(r => setBeats(r.data.items || []))
      .catch(() => {});
  }, [user?.id]);

  const cells = useMemo(() => daysGrid(month), [month]);
  const dayMap = useMemo(() => {
    const m = {};
    (data?.days || []).forEach(d => { m[d.date] = d; });
    return m;
  }, [data]);
  // List view — one row per scheduled day, full beat name (never truncated)
  // instead of the calendar's tiny cell, which cuts long beat names down to
  // a handful of characters (e.g. "MAINT-R…").
  const scheduledDays = useMemo(() => {
    return (data?.days || [])
      .filter(d => d.beat_id)
      .map(d => ({ ...d, beat: beats.find(b => b.id === d.beat_id) }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [data, beats]);

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const submitRequest = async () => {
    if (!proposedBeat) { toast.error("Pick a beat"); return; }
    if (!reason.trim() || reason.trim().length < 3) { toast.error("Reason ≥ 3 chars"); return; }
    setSubmitting(true);
    try {
      await api.post("/pjp/requests", {
        requested_date: selectedDate,
        requested_beat_id: proposedBeat,
        reason,
      });
      toast.success("Change request submitted");
      setProposeOpen(false); setProposedBeat(""); setReason("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="p-3 pb-24 space-y-3" data-testid="pjp-mobile-page">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-[#6D2C91]" /> My PJP
        </h1>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => shiftMonth(-1)} data-testid="pjp-m-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-xs font-semibold min-w-[100px] text-center" data-testid="pjp-m-month">
            {new Date(month + "-01").toLocaleDateString(undefined, { month:"short", year:"numeric" })}
          </span>
          <Button variant="ghost" size="icon" onClick={() => shiftMonth(1)} data-testid="pjp-m-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* View toggle — List shows the full beat name per day; Calendar is
          the compact month grid (beat names truncate there — long names
          like "MAINT-R…" don't fit a 14px-tall cell). */}
      <div className="flex rounded-lg border border-neutral-200 p-0.5 w-fit" data-testid="pjp-m-view-toggle">
        <button onClick={() => setView("list")}
                className={"flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium "
                  + (view === "list" ? "bg-[#6D2C91] text-white" : "text-neutral-600")}
                data-testid="pjp-m-view-list">
          <List className="h-3.5 w-3.5" /> List
        </button>
        <button onClick={() => setView("calendar")}
                className={"flex items-center gap-1 px-3 py-1 rounded-md text-xs font-medium "
                  + (view === "calendar" ? "bg-[#6D2C91] text-white" : "text-neutral-600")}
                data-testid="pjp-m-view-calendar">
          <Grid3x3 className="h-3.5 w-3.5" /> Calendar
        </button>
      </div>

      {loading && <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" /></div>}
      {!loading && (
        <>
          {view === "list" ? (
            <div className="space-y-1.5" data-testid="pjp-m-list">
              {scheduledDays.length === 0 && (
                <Card className="border-neutral-200">
                  <CardContent className="py-6 text-center text-xs text-neutral-400">
                    No beats scheduled this month.
                  </CardContent>
                </Card>
              )}
              {scheduledDays.map(day => {
                const dt = new Date(day.date + "T00:00:00");
                const weekday = dt.toLocaleDateString(undefined, { weekday: "short" });
                const dayNum = parseInt(day.date.split("-")[2]);
                return (
                  <button key={day.date} onClick={() => setSelectedDate(day.date)}
                          className="w-full flex items-center gap-3 rounded-xl border border-neutral-200 bg-white p-2.5 text-left hover:border-[#6D2C91]/40"
                          data-testid={`pjp-m-list-day-${day.date}`}>
                    <div className="flex flex-col items-center justify-center w-11 h-11 rounded-lg bg-neutral-50 border border-neutral-100 shrink-0">
                      <div className="text-[9px] uppercase text-neutral-400 font-semibold leading-none">{weekday}</div>
                      <div className="text-base font-bold text-neutral-800 leading-tight">{dayNum}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 text-sm font-semibold text-neutral-900">
                        <MapPin className="h-3.5 w-3.5 text-[#6D2C91] shrink-0" />
                        <span className="truncate">{day.beat?.name || day.beat?.code || "—"}</span>
                      </div>
                      {day.beat?.code && day.beat?.name && (
                        <div className="text-[11px] text-neutral-400">{day.beat.code}</div>
                      )}
                    </div>
                    {day.source && (
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded text-white text-[9px] font-bold shrink-0"
                            style={{ background: SRC_COLOR[day.source] }}
                            data-testid={`pjp-m-list-src-${day.date}`}>
                        {SRC_LETTER[day.source]}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <Card className="border-neutral-200 rounded-2xl">
              <CardContent className="p-2">
                <div className="grid grid-cols-7 gap-1" data-testid="pjp-m-grid">
                  {["S","M","T","W","T","F","S"].map((d, i) => (
                    <div key={i} className="text-[10px] uppercase text-neutral-500 font-semibold text-center py-0.5">{d}</div>
                  ))}
                  {cells.map((iso, idx) => {
                    if (!iso) return <div key={idx} className="h-14 bg-neutral-50 rounded" />;
                    const day = dayMap[iso];
                    const b = day?.beat_id ? beats.find(x => x.id === day.beat_id) : null;
                    return (
                      <button key={iso} onClick={() => setSelectedDate(iso)}
                               className="h-14 rounded border border-neutral-200 p-1 text-left flex flex-col hover:border-[#6D2C91]/40"
                               data-testid={`pjp-m-day-${iso}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-semibold text-neutral-700">{parseInt(iso.split("-")[2])}</span>
                          {day?.source && (
                            <span className="inline-flex items-center justify-center w-3 h-3 rounded text-white text-[8px] font-bold"
                                  style={{ background: SRC_COLOR[day.source] }}
                                  data-testid={`pjp-m-src-${iso}`}>
                              {SRC_LETTER[day.source]}
                            </span>
                          )}
                        </div>
                        <div className="text-[9px] text-neutral-600 truncate mt-auto">{b?.name || b?.code || ""}</div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* My requests */}
          <div className="space-y-1.5" data-testid="pjp-m-requests">
            <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">My PJP requests</div>
            {requests.length === 0 && (
              <Card className="border-neutral-200"><CardContent className="py-3 text-center text-xs text-neutral-400">No requests yet</CardContent></Card>
            )}
            {requests.map(r => {
              // PJP hotfix (2026-02): show beat NAME (fallback code) rather
              // than raw beat code like "BEAT-RJ-JAIPUR-01".
              const rBeat = beats.find(b => b.id === r.requested_beat_id);
              const rBeatLabel = rBeat?.name || rBeat?.code || "—";
              return (
              <Card key={r.id} className="border-neutral-200" data-testid={`pjp-m-req-${r.id}`}>
                <CardContent className="p-2 flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold">{r.requested_date}</div>
                    <div className="text-[11px] text-neutral-500 truncate"
                         data-testid={`pjp-m-req-beat-${r.id}`}>→ {rBeatLabel}</div>
                  </div>
                  <Badge className={"text-[9px] " + (BADGE[r.status] || "")}>
                    {r.status === "submitted" ? "with mgr" : r.status === "manager_approved" ? "with admin" :
                     r.status === "admin_approved" ? "approved" : "rejected"}
                  </Badge>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Day sheet */}
      <Sheet open={!!selectedDate} onOpenChange={v => !v && setSelectedDate(null)}>
        <SheetContent side="bottom" className="rounded-t-2xl" data-testid="pjp-m-day-sheet">
          <SheetHeader>
            <SheetTitle>{selectedDate}</SheetTitle>
            <SheetDescription>
              {(() => {
                const day = dayMap[selectedDate];
                if (!day?.beat_id) return "No beat assigned.";
                const b = beats.find(x => x.id === day.beat_id);
                return b ? `Current: ${b.code} · ${b.name}` : "Loading…";
              })()}
            </SheetDescription>
          </SheetHeader>
          <div className="pt-3">
            {/* A past day is already over — re-planning it makes no sense,
                and the server rejects the submission anyway. */}
            {selectedDate && selectedDate < new Date().toISOString().slice(0, 10) ? (
              <p className="text-xs text-neutral-400 text-center py-2">
                This date has passed — change requests are only for today or a future day.
              </p>
            ) : (
              <Button className="w-full bg-[#6D2C91] text-white hover:bg-[#5b2478]"
                      onClick={() => { setProposeOpen(true); }}
                      data-testid="pjp-m-propose-btn">
                Propose change
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Propose change sheet */}
      <Sheet open={proposeOpen} onOpenChange={setProposeOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl" data-testid="pjp-m-propose-sheet">
          <SheetHeader>
            <SheetTitle>Change request for {selectedDate}</SheetTitle>
            <SheetDescription>Goes to your manager first, then admin.</SheetDescription>
          </SheetHeader>
          <div className="space-y-3 pt-3">
            <div>
              <Label className="text-xs">Beat</Label>
              <select className="w-full border rounded-md p-2 text-sm"
                       value={proposedBeat}
                       onChange={e => setProposedBeat(e.target.value)}
                       data-testid="pjp-m-propose-beat">
                <option value="">— pick a beat —</option>
                {beats.map(b => <option key={b.id} value={b.id}>{b.code} · {b.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Reason</Label>
              <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
                         placeholder="Why the change?"
                         data-testid="pjp-m-propose-reason" />
            </div>
            <Button className="w-full bg-[#6D2C91] text-white hover:bg-[#5b2478]"
                    onClick={submitRequest} disabled={submitting}
                    data-testid="pjp-m-propose-submit">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
              Submit
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
